import React, { useEffect } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from '@americanexpress/dls-react';
import { IconChevronRight } from '@americanexpress/dls-icons';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn } from '@/helpers/utils';
import { Card, termsAnchorTags, termsModalSessionKey } from '@/constants';
import { useAnalyticsContext } from '@/context/analyticsContext';

import { useModal } from '@/context/modalContext';
import LFLink from './LFLink';
import { modalTypes } from './modals/constants';
import touchTargetStyles from './TargetTouchSize.module.scss';

const Footer = ({ className }) => {
  const { customTrack } = useAnalyticsContext();
  const { setModalName } = useModal();
  const { product, airportCode, loungeId, pathname } = useRouteParams();
  const { locale } = useSelector(getLocaleData);

  const {
    footer: { additionalInfo, popularAirports, homePage },
  } = useCommonContent();

  const handleLinkClick = (event, link) => {
    event.preventDefault();
    setModalName(modalTypes.TERMS_MODAL);
    customTrack({ event, element: link.element, detail: link.label });
  };

  // The popular airports list will ONLY apply for the US Platinum and Centurion for now, since not all LF versions have the same airports included.
  const isUSPlatinumOrCenturion =
    locale === 'en-US' &&
    (product === Card.Platinum || product === Card.Centurion);

  // DO NOT display Additional Info link when on the Lounge details page
  const isLoungeDetailsPage = !!loungeId;
  const isHomePage =
    !airportCode && !loungeId && !pathname?.includes('all-airports');

  const footerLinks = [
    {
      // the additional information section will be displayed for all cards
      display: !isLoungeDetailsPage,
      heading: additionalInfo?.header,
      id: 'additional-information',
      links: [
        {
          element: 'terms and conditions link',
          label:
            isHomePage && homePage?.termsLabel
              ? homePage?.termsLabel
              : additionalInfo?.termsLabel,
        },
      ].map((link) => ({
        ...link,
        // add link.label check for now to avoid extra link when switching countries
        label: link.label && (
          <li key={link.label} className="tw-mr-5dot5 tw-inline">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- link to open the additional information modals */}
            <Link
              className={clsx(
                'tw-py-0 tw-text-link',
                touchTargetStyles.touchTarget,
                touchTargetStyles.termsLink
              )}
              onClick={(e) => handleLinkClick(e, link)}
              reactlytics={{ disable: true }}
            >
              {link.label}
            </Link>
          </li>
        ),
      })),
    },
    {
      display: isUSPlatinumOrCenturion,
      heading: popularAirports?.header,
      id: 'popular-airports',
      links: popularAirports?.links?.map((link) => ({
        ...link,
        label: (
          <LFLink
            key={`popular-airport-${link.iata}`}
            detail={`popular-airport-${link.iata}`}
            className={clsx(
              'tw-items-center tw-py-2dot5 tw-text-link hover_tw-underline',
              touchTargetStyles.touchTarget
            )}
            to={`${product}/${link.iata}/`}
            element="footer"
          >
            {link.label}
            <IconChevronRight
              size="xs"
              isFilled={true}
              className={cn('tw-ml-1 tw-mr-1 tw-text-blue-bright-shade-600-s8')}
            />
          </LFLink>
        ),
      })),
    },
  ];

  useEffect(() => {
    if (global.BROWSER) {
      const urlAnchor = window.location.hash.slice(1);
      if (
        termsAnchorTags.includes(urlAnchor) &&
        !isLoungeDetailsPage &&
        window.sessionStorage.getItem(termsModalSessionKey) !== '1'
      ) {
        setModalName(modalTypes.TERMS_MODAL);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only open the modal on initial load if there is an anchor tag
  }, []);

  // do not display the footer on the lounge details page for non US Platinum & Centurion cards
  if (isLoungeDetailsPage && !isUSPlatinumOrCenturion) {
    return null;
  }

  return (
    <section
      className={cn(
        'tw-mx-auto tw-mb-10 tw-max-w-screen-x2l tw-px-4 md_tw-px-6 lg_tw-px-8 xl_tw-px-10',
        className
      )}
    >
      <hr className="tw-my-10 tw-border-t tw-border-gray-300" />
      {footerLinks
        .filter((link) => link.display)
        .map(({ heading, links, id }) => (
          <div key={heading} className="tw-mb-4">
            <h3 id={id} className="tw-heading-xs-bold tw-mb-2 tw-text-gray-700">
              {heading}
            </h3>
            {id === 'popular-airports' ? (
              <div className="md_ts-grid-cols-2 tw-grid tw-gap-x-6 lg_tw-grid-cols-3">
                {links.map((link) => link.label)}
              </div>
            ) : (
              <ul>{links.map((link) => link.label)}</ul>
            )}
          </div>
        ))}
    </section>
  );
};

Footer.propTypes = {
  className: PropTypes.string,
};

export default Footer;
